import { Injectable, inject } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MainNavigationService } from '../main-navigation.service';
import { FeatureFlagsService } from '../feature-flags.service';
import { TeaItemProduct } from '@redirector-service/database-module';
import { NGXLogger } from 'ngx-logger';

export interface TeaItemResolverData {
  teaItemProduct: TeaItemProduct | null;
  featureLowStockNotice: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TeaItemResolver implements Resolve<TeaItemResolverData> {
  private mainNavigationService = inject(MainNavigationService);
  private featureFlagsService = inject(FeatureFlagsService);
  private logger = inject(NGXLogger);


  resolve(route: ActivatedRouteSnapshot): Observable<TeaItemResolverData> {
    const teaItemSlug = route.params['tea-item-slug'];
    
    if (!teaItemSlug) {
      return of({
        teaItemProduct: null,
        featureLowStockNotice: false,
        errors: ['Tea item slug not found in route']
      });
    }

    // Combine API call and feature flag check
    return combineLatest([
      this.mainNavigationService.getTeaItemProduct(teaItemSlug).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving the tea item:', error);
          return of(null);
        })
      ),
      this.featureFlagsService.getBooleanValue$('disable-low-stock', true).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving feature flag:', error);
          return of(true); // Default to disabled if error
        })
      )
    ]).pipe(
      map(([teaItemProduct, disableLowStock]) => {
        const errors: string[] = [];
        
        if (!teaItemProduct) {
          errors.push('An error occurred while retrieving the tea item.');
        }

        return {
          teaItemProduct,
          featureLowStockNotice: !disableLowStock,
          errors
        };
      })
    );
  }
}
